/**
 * DEFCON TERMINAL - Text Rendering System
 * Handles typewriter effects, color parsing, and line wrapping
 */

import { TIMING, ASCII_DIMENSIONS, TERMINAL_COLORS } from '@shared/constants';
import { COLOR_CODES } from './Terminal';

/** Render speed presets */
export type RenderSpeed = 'instant' | 'fast' | 'normal' | 'slow';

/** Render options */
export interface RenderOptions {
  speed?: number | RenderSpeed;
  parseColors?: boolean;
  animate?: boolean;
  wrapWidth?: number;
  addGlow?: boolean;
  rgbSeparation?: boolean;
}

/** Text renderer configuration */
export interface TextRendererConfig {
  defaultSpeed?: number;
  maxLineWidth?: number;
  animationEnabled?: boolean;
}

/** Speed preset values in ms */
const SPEED_PRESETS: Record<RenderSpeed, number> = {
  instant: 0,
  fast: TIMING.fastTypingSpeed,
  normal: TIMING.typingSpeed,
  slow: TIMING.slowTypingSpeed,
};

/**
 * TextRenderer class
 * Handles all text rendering with typewriter effects and styling
 */
export class TextRenderer {
  private container: HTMLElement;
  private config: Required<TextRendererConfig>;
  private currentAnimation: AbortController | null = null;

  constructor(container: HTMLElement, config: TextRendererConfig = {}) {
    this.container = container;
    this.config = {
      defaultSpeed: config.defaultSpeed ?? TIMING.typingSpeed,
      maxLineWidth: config.maxLineWidth ?? ASCII_DIMENSIONS.terminalWidth,
      animationEnabled: config.animationEnabled ?? true,
    };
  }

  /**
   * Type text with typewriter effect
   */
  public async typeText(
    element: HTMLElement,
    text: string,
    options: RenderOptions = {}
  ): Promise<void> {
    const speed = this.resolveSpeed(options.speed);
    const parseColors = options.parseColors ?? true;
    const wrapWidth = options.wrapWidth ?? this.config.maxLineWidth;

    // Cancel any existing animation
    this.cancelAnimation();

    // Create abort controller for this animation
    this.currentAnimation = new AbortController();
    const signal = this.currentAnimation.signal;

    // Wrap text if needed
    const wrappedText = this.wrapText(text, wrapWidth);

    // Instant render if speed is 0
    if (speed === 0 || !this.config.animationEnabled) {
      if (parseColors) {
        element.innerHTML = this.parseColorCodes(wrappedText);
      } else {
        element.textContent = wrappedText;
      }
      return;
    }

    // Typewriter effect
    try {
      await this.animateTypewriter(element, wrappedText, speed, parseColors, signal);
    } catch {
      // Animation was cancelled
    }
  }

  /**
   * Animate typewriter effect character by character
   */
  private async animateTypewriter(
    element: HTMLElement,
    text: string,
    speed: number,
    parseColors: boolean,
    signal: AbortSignal
  ): Promise<void> {
    element.innerHTML = '';

    // Parse color codes into segments
    const segments = parseColors ? this.parseIntoSegments(text) : [{ text, className: '' }];

    for (const segment of segments) {
      if (signal.aborted) throw new Error('Animation cancelled');

      // Create span for this segment
      const span = document.createElement('span');
      if (segment.className) {
        span.className = segment.className;
      }
      element.appendChild(span);

      // Type each character
      for (const char of segment.text) {
        if (signal.aborted) throw new Error('Animation cancelled');

        span.textContent += char;
        await this.delay(speed);
      }
    }
  }

  /**
   * Parse text into segments with color classes
   */
  private parseIntoSegments(text: string): Array<{ text: string; className: string }> {
    const segments: Array<{ text: string; className: string }> = [];
    let currentText = '';
    let currentClass = '';
    let remaining = text;

    while (remaining.length > 0) {
      // Check for color codes at current position
      let foundCode = false;

      for (const [code, className] of Object.entries(COLOR_CODES)) {
        if (remaining.startsWith(code)) {
          // Save current segment if any
          if (currentText.length > 0) {
            segments.push({ text: currentText, className: currentClass });
            currentText = '';
          }

          // Update class
          currentClass = className;
          remaining = remaining.slice(code.length);
          foundCode = true;
          break;
        }
      }

      if (!foundCode) {
        // Add character to current text
        currentText += remaining[0];
        remaining = remaining.slice(1);
      }
    }

    // Add final segment
    if (currentText.length > 0) {
      segments.push({ text: currentText, className: currentClass });
    }

    return segments;
  }

  /**
   * Parse color codes and return HTML string
   */
  private parseColorCodes(text: string): string {
    let result = this.escapeHtml(text);
    let currentClass = '';

    for (const [code, className] of Object.entries(COLOR_CODES)) {
      const escapedCode = this.escapeRegex(code);
      const regex = new RegExp(escapedCode, 'g');

      if (className === '') {
        result = result.replace(regex, currentClass ? '</span>' : '');
        currentClass = '';
      } else {
        result = result.replace(regex, () => {
          const closeTag = currentClass ? '</span>' : '';
          currentClass = className;
          return `${closeTag}<span class="${className}">`;
        });
      }
    }

    if (currentClass) {
      result += '</span>';
    }

    return result;
  }

  /**
   * Wrap text at specified width
   */
  public wrapText(text: string, maxWidth: number = this.config.maxLineWidth): string {
    if (text.length <= maxWidth) {
      return text;
    }

    const lines: string[] = [];
    const words = text.split(' ');
    let currentLine = '';

    for (const word of words) {
      // Check if word itself is longer than max width
      if (word.length > maxWidth) {
        // Push current line if any
        if (currentLine.length > 0) {
          lines.push(currentLine);
          currentLine = '';
        }

        // Break long word
        let remaining = word;
        while (remaining.length > maxWidth) {
          lines.push(remaining.slice(0, maxWidth));
          remaining = remaining.slice(maxWidth);
        }
        currentLine = remaining;
      } else if (currentLine.length + word.length + 1 <= maxWidth) {
        // Word fits on current line
        currentLine += (currentLine.length > 0 ? ' ' : '') + word;
      } else {
        // Word doesn't fit, start new line
        if (currentLine.length > 0) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    }

    // Push final line
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    return lines.join('\n');
  }

  /**
   * Resolve speed from preset or number
   */
  private resolveSpeed(speed?: number | RenderSpeed): number {
    if (speed === undefined) {
      return this.config.defaultSpeed;
    }
    if (typeof speed === 'number') {
      return speed;
    }
    return SPEED_PRESETS[speed];
  }

  /**
   * Cancel current animation
   */
  public cancelAnimation(): void {
    if (this.currentAnimation) {
      this.currentAnimation.abort();
      this.currentAnimation = null;
    }
  }

  /**
   * Render text instantly
   */
  public renderInstant(element: HTMLElement, text: string, parseColors: boolean = true): void {
    if (parseColors) {
      element.innerHTML = this.parseColorCodes(text);
    } else {
      element.textContent = text;
    }
  }

  /**
   * Create styled text span
   */
  public createStyledText(text: string, className: string = ''): HTMLSpanElement {
    const span = document.createElement('span');
    span.textContent = text;
    if (className) {
      span.className = className;
    }
    return span;
  }

  /**
   * Add glow effect to element
   */
  public addGlowEffect(element: HTMLElement, intensity: 'low' | 'medium' | 'high' = 'medium'): void {
    const glowClasses: Record<string, string> = {
      low: 'color-dim',
      medium: 'color-green',
      high: 'color-bright',
    };
    element.classList.add(glowClasses[intensity]);
  }

  /**
   * Add RGB separation effect
   */
  public addRGBSeparation(element: HTMLElement, heavy: boolean = false): void {
    element.classList.add(heavy ? 'rgb-heavy' : 'rgb-separation');
  }

  /**
   * Render ASCII art (preserves spacing)
   */
  public renderASCII(element: HTMLElement, ascii: string): void {
    element.style.whiteSpace = 'pre';
    element.style.fontFamily = 'monospace';
    element.textContent = ascii;
  }

  /**
   * Render ASCII art with typewriter effect
   */
  public async typeASCII(element: HTMLElement, ascii: string, lineDelay: number = 50): Promise<void> {
    const lines = ascii.split('\n');
    element.style.whiteSpace = 'pre';
    element.style.fontFamily = 'monospace';
    element.textContent = '';

    for (const line of lines) {
      element.textContent += line + '\n';
      await this.delay(lineDelay);
    }
  }

  /**
   * Scramble text effect (for decryption animation)
   */
  public async scrambleReveal(
    element: HTMLElement,
    finalText: string,
    iterations: number = 10,
    delay: number = 50
  ): Promise<void> {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    const length = finalText.length;

    for (let i = 0; i < iterations; i++) {
      let scrambled = '';
      const revealIndex = Math.floor((i / iterations) * length);

      for (let j = 0; j < length; j++) {
        if (j < revealIndex) {
          scrambled += finalText[j];
        } else if (finalText[j] === ' ') {
          scrambled += ' ';
        } else {
          scrambled += chars[Math.floor(Math.random() * chars.length)];
        }
      }

      element.textContent = scrambled;
      await this.delay(delay);
    }

    element.textContent = finalText;
  }

  /**
   * Render progress bar
   */
  public renderProgressBar(progress: number, width: number = 40): string {
    const filled = Math.floor((progress / 100) * width);
    const empty = width - filled;
    return '[' + '='.repeat(filled) + ' '.repeat(empty) + '] ' + progress.toFixed(0) + '%';
  }

  /**
   * Escape HTML special characters
   */
  private escapeHtml(text: string): string {
    const escapeMap: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return text.replace(/[&<>"']/g, char => escapeMap[char]);
  }

  /**
   * Escape regex special characters
   */
  private escapeRegex(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Set default typing speed
   */
  public setDefaultSpeed(speed: number): void {
    this.config.defaultSpeed = speed;
  }

  /**
   * Enable/disable animations
   */
  public setAnimationEnabled(enabled: boolean): void {
    this.config.animationEnabled = enabled;
  }

  /**
   * Get current configuration
   */
  public getConfig(): Readonly<Required<TextRendererConfig>> {
    return { ...this.config };
  }
}

export default TextRenderer;
